import {
  Box,
  Checkbox,
  CheckboxProps,
  Divider,
  FormControlLabel,
  Link,
  LinkProps,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { MxheroShareConfig } from "@dashboard-v3/api";

type Permission =
  | MxheroShareConfig["ownerPermissions"][number]
  | "noModification";

interface Props {
  permissions: MxheroShareConfig["ownerPermissions"];
  onChange: (update: Partial<MxheroShareConfig>) => void;
}

const fullShareSettingsPermissions = [
  "allowDownloadOption",
  "changeExpiration",
  "changePassword",
  "revokeAccess",
  "deleteFiles",
] as const;

export default function EndUserPermissions(props: Props) {
  const { t } = useTranslation("mxheroShares");
  const { permissions = [] } = props;
  const isFullPermissionsSet =
    permissions.length === fullShareSettingsPermissions.length;

  const setFullPermissions: LinkProps["onClick"] = event => {
    event.preventDefault();
    props.onChange({ ownerPermissions: [...fullShareSettingsPermissions] });
  };

  const updateShareSettingsAccess: CheckboxProps["onChange"] = event => {
    const { name, checked } = event.target;

    if (checked && name === "noModification") {
      return props.onChange({ ownerPermissions: [] });
    }

    if (checked) {
      permissions.push(name as Exclude<Permission, "noModification">);
      // prevent duplicates
      return props.onChange({
        ownerPermissions: [...new Set(permissions || [])],
      });
    }

    // Unchecked
    if (name === "noModification") return;

    const selection = permissions?.filter(level => level !== name);
    props.onChange({ ownerPermissions: [...new Set(selection)] });
  };

  const updateShareBrowsingSettings: CheckboxProps["onChange"] = e => {
    const { name, checked } = e.target;

    if (!checked) {
      return props.onChange({
        ownerPermissions: permissions.filter(p => p !== name),
      });
    }

    props.onChange({
      ownerPermissions: [
        ...permissions,
        name as Exclude<Permission, "noModification">,
      ],
    });
  };

  return (
    <Box>
      <Typography variant="subtitle2">
        {t("settings.endUserPerm.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {t("settings.endUserPerm.subtitle")}
      </Typography>
      <Typography variant="caption" color="textSecondary" component="p">
        {t("settings.endUserPerm.propagationNotice")}
      </Typography>

      <Box
        mt="1rem"
        mb=".5rem"
        ml=".5rem"
        display="flex"
        flexDirection="column"
      >
        <Box mb="1rem">
          {isFullPermissionsSet ? (
            <Typography variant="body2" color="textSecondary">
              {t("settings.endUserPerm.fullSet")}
            </Typography>
          ) : (
            <Link href="#" onClick={setFullPermissions}>
              {t("settings.endUserPerm.setFull")}
            </Link>
          )}
        </Box>

        <Box display={"flex"} flexDirection="column" gridGap="1.4rem">
          {/* Change expiration date */}
          <FormControlLabel
            control={
              <Checkbox
                name="changeExpiration"
                checked={permissions.includes("changeExpiration")}
                onChange={updateShareSettingsAccess}
                color="primary"
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.endUserPerm.expirationTitle")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.endUserPerm.expirationExplain")}
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                name="allowDownloadOption"
                checked={permissions.includes("allowDownloadOption")}
                onChange={updateShareSettingsAccess}
                color="primary"
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.endUserPerm.allowDownloadTitle")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.endUserPerm.allowDownloadExplain")}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                name="changePassword"
                checked={permissions.includes("changePassword")}
                onChange={updateShareSettingsAccess}
                color="primary"
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.endUserPerm.passTitle")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.endUserPerm.passExplain")}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                name="revokeAccess"
                checked={permissions.includes("revokeAccess")}
                onChange={updateShareSettingsAccess}
                color="primary"
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.endUserPerm.revokeTitle")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.endUserPerm.revokeExplain")}
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                name="deleteFiles"
                checked={permissions.includes("deleteFiles")}
                onChange={updateShareSettingsAccess}
                color="primary"
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.endUserPerm.deleteFileTitle")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.endUserPerm.deleteFileExplain")}
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                name="noModification"
                color="primary"
                checked={
                  permissions.filter(p => p !== "preventFileDownloads")
                    .length === 0
                }
                onChange={updateShareSettingsAccess}
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.endUserPerm.noEditTitle")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.endUserPerm.noEditExplain")}
                </Typography>
              </Box>
            }
          />
          <Divider />
          <Box>
            <Typography variant="subtitle2">
              {t("settings.browseRestrictions.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("settings.browseRestrictions.desc")}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                name="preventFileDownloads"
                color="primary"
                checked={permissions.includes("preventFileDownloads")}
                onChange={updateShareBrowsingSettings}
              />
            }
            label={
              <Box ml=".8rem">
                <Typography variant="body2">
                  {t("settings.browseRestrictions.downloadsLabel")}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("settings.browseRestrictions.downloadsDesc")}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}
